import { ApiError } from "@/api/fetch";
import { ClipboardRequestError } from "@/features/clipboard/api/clipboard";
import { toast } from "sonner";

export type LimitFeature = "chat" | "playground" | "clipboardSearch" | "uploads";

export type LimitKind =
  | "quotaPrompts"
  | "quotaMaps"
  | "quotaStorage"
  | "rate"
  | "concurrency";

export const isQuotaLimitKind = (kind: LimitKind): boolean =>
  kind === "quotaPrompts" || kind === "quotaMaps" || kind === "quotaStorage";

export const classifyLimitError = (
  status: number,
  detail: string,
): LimitKind | null => {
  const text = detail.toLowerCase();
  const looksLimited =
    status === 429 ||
    text.includes("rate limit") ||
    text.includes("quota") ||
    text.includes("too many concurrent") ||
    text.includes("daily prompt") ||
    text.includes("daily limit") ||
    text.includes("storage limit") ||
    text.includes("document limit");
  if (!looksLimited) return null;

  if (
    text.includes("storage limit") ||
    text.includes("document limit reached") ||
    text.includes("clipboard storage") ||
    text.includes("not enough clipboard")
  ) {
    return "quotaStorage";
  }
  if (text.includes("map/verify") || text.includes("daily limit on map")) {
    return "quotaMaps";
  }
  if (text.includes("daily prompt") || text.includes("query limit")) {
    return "quotaPrompts";
  }
  if (text.includes("too many concurrent") || text.includes("already running")) {
    return "concurrency";
  }
  return "rate";
};

export const userFacingLimitMessage = (
  feature: LimitFeature,
  kind: LimitKind,
): string => {
  switch (kind) {
    case "quotaPrompts":
      return "Daily query limit reached.";
    case "quotaMaps":
      return "Daily map limit reached.";
    case "quotaStorage":
      return "Workspace storage is full.";
    case "concurrency":
      return feature === "playground"
        ? "Too many playground commands are running. Wait for one to finish."
        : "Too many requests are running. Wait for one to finish.";
    case "rate":
      if (feature === "chat") {
        return "Rate limit for chat exceeded. Please wait a moment and try again.";
      }
      if (feature === "playground") {
        return "Rate limit for playground exceeded. Please wait a moment and try again.";
      }
      if (feature === "clipboardSearch") {
        return "Rate limit for clipboard search exceeded. Please wait a moment and try again.";
      }
      return "Rate limit for uploads exceeded. Please wait a moment and try again.";
  }
};

const statusAndDetail = (err: unknown): { status: number; detail: string } => {
  if (err instanceof ApiError || err instanceof ClipboardRequestError) {
    return { status: err.status, detail: err.message };
  }
  if (err instanceof Error) {
    return { status: 0, detail: err.message };
  }
  return { status: 0, detail: "" };
};

export const describeLimitError = (
  err: unknown,
  feature: LimitFeature,
): { kind: LimitKind; message: string } | null => {
  const { status, detail } = statusAndDetail(err);
  const kind = classifyLimitError(status, detail);
  if (!kind) return null;
  return { kind, message: userFacingLimitMessage(feature, kind) };
};

/** Toast a friendly 429, refetch usage on quota, return the user-facing message. */
export const reportLimitError = (
  err: unknown,
  feature: LimitFeature,
  opts: { refetchUsage: () => void; quotaAlertVisible?: boolean },
): string | null => {
  const described = describeLimitError(err, feature);
  if (!described) return null;
  if (isQuotaLimitKind(described.kind)) opts.refetchUsage();
  const skipToast =
    isQuotaLimitKind(described.kind) && Boolean(opts.quotaAlertVisible);
  if (!skipToast) {
    toast.error(described.message);
  }
  return described.message;
};
